import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Divider,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@material-ui/core';
import { useSelector } from '@store';
import {
  CapaPcbServiceType,
  CapaQuotationUserType,
  CapaQuotationServiceType,
  CapaQuotationUserRfqDetail,
  CapaQuotationPartnerStrategy
} from '@generated/graphql';
import {
  ConditionalRender,
  CustomizedTypography
} from '@components/Customized';
import {
  PurchaseOrderHeader,
  PurchaseOrderStepper,
  PurchaseOrderHistoryBar,
  PurchaseOrderFixedBottomCTA
} from '../Common';
import {
  QuotationViewItemsTable,
  QuotationViewPcbItemsTable
} from '../../ReceivedQuotationForm';
import useStyles from './useStyles';
import PaymentMethod from './Payment/Method';
import { CreateOrderFormSection } from './styles';
import { PurchaseOrderBaseData } from './useBaseData';
import CreateOrderUserInfo from './CreateOrderUserInfo';
import CreateOrderDeliveryInfo from './CreateOrderDeliverInfo';
import CreateOrderMoldTransfer from './CreateOrderMoldTransferInfo';
import CreatePurchaseOrderDropdown from './CreatePurchaseOrderDropdown';
import CreateOrderDraftEditCondition from './CreateOrderDraftEditCondition';
import CreatePurchaseOrderListDrawer from './CreatePurchaseOrderListDrawer';
import { getPriceDatas } from './utils/getTotalAmount';
import NotFoundPartnerSubmall from './NotFoundPartnerSubmall';

interface Props {
  loading: boolean;
  rfqDetail?: CapaQuotationUserRfqDetail;
  data?: PurchaseOrderBaseData;
  partner?: Omit<CapaQuotationPartnerStrategy, 'email'>;
  submitButton: React.ReactNode;
  chatRoomId?: string;
  onCloseAtDashboard?: () => void;
}

const CreatePurchaseOrderPresenter = ({
  loading,
  data,
  partner,
  rfqDetail,
  submitButton,
  chatRoomId,
  onCloseAtDashboard
}: Props) => {
  const isDashboard = window.location.pathname.includes('/dashboard');
  const classes = useStyles();
  const matches = useMediaQuery(useTheme().breakpoints.down('md'));
  const purchaseOrderId = useSelector(
    (state) => state.documents?.selectedPurchaseOrderId
  );
  const quotationId = useSelector(
    (state) => state.documents?.selectedQuotationId
  );
  const { reset, register, watch } = useFormContext<PurchaseOrderBaseData>();
  const clientManagerName = watch('managerName');
  const client = useSelector((state) => state.client);
  const clientOrganizationName = client.organizationName;
  const clientCompanyRegistrationNumber = client.companyRegistrationNumber;
  const isWithdrawn = partner?.status === 'DELETED';
  const serviceType = rfqDetail?.serviceType;
  const projectName = rfqDetail?.projectName ?? '';
  const isPcb = serviceType === CapaQuotationServiceType.Pcb;
  const pcbServices = rfqDetail?.pcb?.services ?? [];
  const hasCircuitDesign = pcbServices.some(
    (service) => service.serviceType === CapaPcbServiceType.CircuitDesign
  );
  const priceData = getPriceDatas(data);
  const hasMold = [
    CapaQuotationServiceType.Inj,
    CapaQuotationServiceType.Casting
  ].includes(serviceType as CapaQuotationServiceType);
  const noDrawingFile =
    CapaQuotationServiceType.Design ===
      (serviceType as CapaQuotationServiceType) ||
    (isPcb && hasCircuitDesign);
  const hasNormalItems = (data?.items && data.items.length > 0) ?? false;
  const hasFabricationItems =
    (data?.pcbFabricationItems && data.pcbFabricationItems.length > 0) ?? false;
  const hasAssemblyItems =
    (data?.pcbAssemblyItems && data.pcbAssemblyItems.length > 0) ?? false;

  useEffect(() => {
    if (data) {
      reset({
        ...data,
        shipDate: data?.shipDate ? data.shipDate : new Date().toISOString()
      });
    }

    //eslint-disable-next-line
  }, [purchaseOrderId, quotationId]);

  return (
    <>
      <NotFoundPartnerSubmall
        chatRoomId={chatRoomId}
        partnerId={partner?.id}
        onCloseAtDashboard={onCloseAtDashboard}
      />

      {!isDashboard && (
        <>
          {!matches && (
            <CreatePurchaseOrderDropdown
              rfqId={rfqDetail?.id}
              quotationPartnerId={partner?.id}
            />
          )}

          {matches && (
            <CreatePurchaseOrderListDrawer
              rfqId={rfqDetail?.id}
              quotationPartnerId={partner?.id}
            />
          )}

          <PurchaseOrderHeader hasPurchaseOrderHistory={Boolean(data)} />

          <PurchaseOrderStepper activeStep={-1} />
        </>
      )}

      {loading && (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      )}

      {!loading && (
        <form id="purchaseOrderForm" className={classes.formContainer}>
          {!isDashboard && (
            <PurchaseOrderHistoryBar
              createdAt={data?.createdAt ?? ''}
              isLatest={data?.latest ?? false}
              isRejected={data?.isRejected ?? false}
            />
          )}

          <input type="hidden" ref={register} name="rfqId" />
          <input type="hidden" ref={register} name="partnerId" />
          <input type="hidden" ref={register} name="quotation" />
          <input type="hidden" ref={register} name="deliveryCost" />

          <CustomizedTypography
            variant="h3"
            fontWeight="700"
            className={classes.projectName}
          >
            {rfqDetail?.projectName}
          </CustomizedTypography>

          <ConditionalRender visible={hasNormalItems}>
            <QuotationViewItemsTable
              items={data?.items ?? []}
              matches={matches}
              noDrawing={noDrawingFile}
            />
          </ConditionalRender>

          <ConditionalRender visible={hasFabricationItems}>
            <QuotationViewPcbItemsTable
              projectName={projectName}
              items={data?.pcbFabricationItems ?? []}
            />
          </ConditionalRender>

          <ConditionalRender visible={hasAssemblyItems}>
            <QuotationViewPcbItemsTable
              projectName={projectName}
              items={data?.pcbAssemblyItems ?? []}
            />
          </ConditionalRender>

          <div style={{ paddingTop: 16 }}>
            <CreateOrderFormSection>
              <div className={classes.priceTotal}>
                <div className={classes.rowBetween}>
                  <CustomizedTypography variant="body1">
                    공급가액
                  </CustomizedTypography>
                  <CustomizedTypography variant="body1" fontWeight="500">
                    {priceData.valueOfSupply.toLocaleString('kr')} 원
                  </CustomizedTypography>
                </div>

                <div className={classes.rowBetween}>
                  <CustomizedTypography variant="body1">
                    부가세(10%)
                  </CustomizedTypography>
                  <CustomizedTypography variant="body1" fontWeight="500">
                    {priceData.tax.toLocaleString('kr')} 원
                  </CustomizedTypography>
                </div>

                <div className={classes.rowBetween}>
                  <CustomizedTypography variant="body1">
                    배송비
                  </CustomizedTypography>
                  <CustomizedTypography variant="body1" fontWeight="500">
                    {priceData.deliveryCost.toLocaleString('kr')} 원
                  </CustomizedTypography>
                </div>

                <Divider />

                <div className={classes.rowBetween}>
                  <CustomizedTypography variant="h4" fontWeight="bold">
                    총 발주금액
                  </CustomizedTypography>
                  <CustomizedTypography
                    variant="h4"
                    fontWeight="bold"
                    color="primary"
                  >
                    {priceData.totalAmount.toLocaleString('kr')} 원
                  </CustomizedTypography>
                </div>
              </div>
            </CreateOrderFormSection>
          </div>

          <Divider className="gutter" />

          <ConditionalRender visible={noDrawingFile}>
            <CreateOrderDraftEditCondition
              isWithdrawn={isWithdrawn}
              times={data?.maxDraftModificationCount ?? 0}
              draftModificationTerms={data?.draftModificationTerms ?? ''}
            />
            <Divider className="gutter" />
          </ConditionalRender>

          <CreateOrderDeliveryInfo
            baseAddress={data?.shipAddress ?? undefined}
            isWithdrawn={isWithdrawn}
          />
          <Divider className="gutter" />

          <ConditionalRender visible={hasMold}>
            <CreateOrderMoldTransfer
              serviceType={rfqDetail?.serviceType as CapaQuotationServiceType}
              baseData={data?.transfer ?? undefined}
            />
            <Divider className="gutter" />
          </ConditionalRender>

          <PaymentMethod />
          <Divider className="gutter" />

          <CreateOrderUserInfo
            userType={CapaQuotationUserType.Partner}
            organizationName={partner?.organizationName}
            companyRegistrationNumber={partner?.companyRegistrationNumber}
            isWithdrawn={isWithdrawn}
          />
          <Divider className="gutter" />

          <div>
            <CreateOrderUserInfo
              userType={CapaQuotationUserType.Client}
              organizationName={
                clientOrganizationName === '개인고객'
                  ? undefined
                  : clientOrganizationName ?? undefined
              }
              companyRegistrationNumber={clientCompanyRegistrationNumber ?? ''}
              managerName={clientManagerName}
              isWithdrawn={isWithdrawn}
            />
          </div>
        </form>
      )}

      <PurchaseOrderFixedBottomCTA height={matches ? 133 : undefined}>
        <div className={classes.cta}>{submitButton}</div>
      </PurchaseOrderFixedBottomCTA>
    </>
  );
};

export default CreatePurchaseOrderPresenter;
